import { isHolidayThemeKey, parseHolidayWindow, type HolidayThemeKey, type HolidayTone, type HolidayWindow } from '../domain/holidayThemes';
import type { AppearanceMode, AppearanceTone } from '../domain/appearance';

export const SHELL_THEME_STORAGE_KEY = 'site-survey:shell-theme:v1';
// Read by the pre-paint script in index.html; keep both in step.
export const HOLIDAY_THEME_STORAGE_KEY = 'site-survey:holiday-theme:v1';
export const HOLIDAY_PREVIEW_STORAGE_KEY = 'site-survey:holiday-preview:v1';
export const HOLIDAY_PREVIEW_EVENT = 'site-survey:holiday-preview';

export type ShellThemeCache = {
  authUserId?: string | null;
  profileId?: string | null;
  colorTheme?: string | null;
  layoutStyle?: string | null;
  appearanceMode?: string | null;
  /** The holiday window this person turned off in Edit profile (docs/holiday-themes.md). */
  holidayOptOut?: string | null;
  updatedAt?: string;
};

function getLocalStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function parseJsonObject(raw: string | null): Record<string, unknown> | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed as Record<string, unknown>
      : null;
  } catch {
    return null;
  }
}

export function readActiveSupabaseAuthUserId(storage: Storage | null = getLocalStorage()): string | null {
  if (!storage) return null;
  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key || !key.startsWith('sb-') || !key.endsWith('-auth-token')) continue;
      const parsed = parseJsonObject(storage.getItem(key));
      const userId =
        (parsed?.user as { id?: unknown } | undefined)?.id ||
        (parsed?.currentSession as { user?: { id?: unknown } } | undefined)?.user?.id ||
        (parsed?.session as { user?: { id?: unknown } } | undefined)?.user?.id;
      if (typeof userId === 'string' && userId.trim()) return userId.trim();
    }
  } catch {
    return null;
  }
  return null;
}

export function readShellThemeCache(
  expectedAuthUserId?: string | null,
  storage: Storage | null = getLocalStorage(),
): ShellThemeCache | null {
  if (!storage) return null;
  const parsed = parseJsonObject(storage.getItem(SHELL_THEME_STORAGE_KEY));
  if (!parsed) return null;
  const cachedAuthUserId = typeof parsed.authUserId === 'string' ? parsed.authUserId : null;
  const activeAuthUserId = expectedAuthUserId || readActiveSupabaseAuthUserId(storage);
  if (cachedAuthUserId && activeAuthUserId && cachedAuthUserId !== activeAuthUserId) return null;
  return {
    authUserId: cachedAuthUserId,
    profileId: typeof parsed.profileId === 'string' ? parsed.profileId : null,
    colorTheme: typeof parsed.colorTheme === 'string' ? parsed.colorTheme : null,
    layoutStyle: typeof parsed.layoutStyle === 'string' ? parsed.layoutStyle : null,
    appearanceMode: typeof parsed.appearanceMode === 'string' ? parsed.appearanceMode : null,
    holidayOptOut: typeof parsed.holidayOptOut === 'string' ? parsed.holidayOptOut : null,
    updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : undefined,
  };
}

/** The last known open holiday window, so the next load paints it before anything is fetched. */
export function readHolidayThemeCache(storage: Storage | null = getLocalStorage()): HolidayWindow | null {
  if (!storage) return null;
  try {
    return parseHolidayWindow(parseJsonObject(storage.getItem(HOLIDAY_THEME_STORAGE_KEY)));
  } catch {
    return null;
  }
}

export function writeHolidayThemeCache(holiday: HolidayWindow | null, storage: Storage | null = getLocalStorage()): void {
  if (!storage) return;
  try {
    if (!holiday) {
      storage.removeItem(HOLIDAY_THEME_STORAGE_KEY);
      return;
    }
    const next = JSON.stringify({ id: holiday.id, themeKey: holiday.themeKey, startsAt: holiday.startsAt, endsAt: holiday.endsAt });
    if (storage.getItem(HOLIDAY_THEME_STORAGE_KEY) !== next) storage.setItem(HOLIDAY_THEME_STORAGE_KEY, next);
  } catch {
    // This cache is only used to avoid first-paint theme flicker.
  }
}

function getSessionStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** An admin's "Preview on my screen": this browser tab only, never saved. */
export function readHolidayPreview(): HolidayThemeKey | null {
  try {
    const value = getSessionStorage()?.getItem(HOLIDAY_PREVIEW_STORAGE_KEY);
    return isHolidayThemeKey(value) ? value : null;
  } catch {
    return null;
  }
}

export function writeHolidayPreview(key: HolidayThemeKey | null): void {
  try {
    const storage = getSessionStorage();
    if (key) storage?.setItem(HOLIDAY_PREVIEW_STORAGE_KEY, key);
    else storage?.removeItem(HOLIDAY_PREVIEW_STORAGE_KEY);
  } catch {
    // The event below still previews this page when session storage is unavailable.
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(HOLIDAY_PREVIEW_EVENT, { detail: key }));
}

export function writeShellThemeCache(
  patch: Omit<ShellThemeCache, 'updatedAt'>,
  storage: Storage | null = getLocalStorage(),
): void {
  if (!storage) return;
  try {
    const existing = parseJsonObject(storage.getItem(SHELL_THEME_STORAGE_KEY)) || {};
    storage.setItem(SHELL_THEME_STORAGE_KEY, JSON.stringify({
      ...existing,
      ...patch,
      updatedAt: new Date().toISOString(),
    }));
  } catch {
    // This cache is only used to avoid first-paint theme flicker.
  }
}

export function applyShellThemeAttributes(params: {
  colorTheme: string;
  layoutStyle: string;
  colorScheme?: string;
  appearanceMode?: AppearanceMode;
  appearanceTone?: AppearanceTone;
  /** A scheduled holiday: `colorTheme` is then its base (green or dark), never the person's saved theme. */
  holiday?: { key: HolidayThemeKey; tone: HolidayTone } | null;
  documentRef?: Document;
}): void {
  const documentRef = params.documentRef ?? (typeof document === 'undefined' ? null : document);
  if (!documentRef) return;
  const root = documentRef.documentElement;
  root.setAttribute('data-theme', params.colorTheme);
  root.setAttribute('data-layout', params.layoutStyle);
  if (params.colorScheme) root.setAttribute('data-color-scheme', params.colorScheme);
  if (params.appearanceMode) root.setAttribute('data-appearance-mode', params.appearanceMode);
  if (params.appearanceTone) {
    root.setAttribute('data-appearance', params.appearanceTone);
    root.setAttribute('data-boot-tone', params.appearanceTone);
  }
  if (params.holiday) {
    root.setAttribute('data-holiday', params.holiday.key);
    root.setAttribute('data-holiday-tone', params.holiday.tone);
  } else {
    root.removeAttribute('data-holiday');
    root.removeAttribute('data-holiday-tone');
  }
  try {
    const styles = documentRef.defaultView?.getComputedStyle(root);
    const bg = (styles?.getPropertyValue('--bg') || '').trim() || '#f6f9fc';
    // index.html carries one per device scheme; the active one depends on the device, so all follow the theme.
    const metas = Array.from(documentRef.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]'));
    if (!metas.length) {
      const meta = documentRef.createElement('meta');
      meta.name = 'theme-color';
      documentRef.head.appendChild(meta);
      metas.push(meta);
    }
    for (const meta of metas) meta.content = bg;
  } catch {
    // Meta theme-color is an enhancement; theme attributes are the source of truth.
  }
}
